import React from 'react';
import { useLazyRender } from '~/hooks/useLazyRender';

interface LazyCategorySectionProps {
  /** Category ID for the section */
  categoryId: string;
  /** Index of the category (first 2 render immediately) */
  index: number;
  /** Theme object for styling */
  theme: {
    cardColor?: string;
    borderRadius?: string;
  } | null;
  /** Children to render when visible */
  children: React.ReactNode;
}

/**
 * Wrapper component for lazy rendering category sections.
 * First 2 categories render immediately (above the fold).
 * Others render when they enter the viewport.
 */
function LazyCategorySectionComponent({
  categoryId,
  index,
  theme,
  children,
}: LazyCategorySectionProps) {
  const { ref, isVisible } = useLazyRender({ rootMargin: '300px' });

  // First 2 categories render immediately (above the fold)
  const renderImmediately = index < 2;

  if (renderImmediately) {
    return (
      <div id={`category-${categoryId}`}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} id={`category-${categoryId}`}>
      {isVisible ? (
        children
      ) : (
        // Skeleton placeholder while loading
        <div
          className="animate-pulse rounded-2xl mb-6"
          style={{
            height: '320px',
            backgroundColor: theme?.cardColor || '#f3f4f6',
            borderRadius: theme?.borderRadius || '1rem',
          }}
        >
          <div className="h-48 sm:h-56 lg:h-64 rounded-2xl bg-gray-300/50 mb-6" />
          <div className="space-y-3 px-4">
            <div className="h-6 bg-gray-300/50 rounded w-1/3" />
            <div className="h-4 bg-gray-300/50 rounded w-2/3" />
          </div>
        </div>
      )}
    </div>
  );
}

LazyCategorySectionComponent.displayName = 'LazyCategorySection';

export const LazyCategorySection = React.memo(LazyCategorySectionComponent);
export default LazyCategorySection;
